import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Field from './Field';
import style from '../../../../scss/components/form.module.scss';
import slugifyAltText from '../../../Helpers/article/slugify';
import CharCounter from '../../../Helpers/CharCounter';

const NBSP_REGEX = /[\u00A0\u202F\u2007]/g;

const normalizeWhitespace = (value) =>
    typeof value === 'string' ? value.replace(NBSP_REGEX, ' ') : value;

const TextInput = ({
    onChange,
    entity,
    regex = null,
    defaultValue = '',
    prefix = null,
    slugify = false,
    disabled = false,
    lockIfNotDraft = false,
    maxLength = null,
    ...props
}) => {
    const isDraft = entity?.is_draft === undefined || entity?.is_draft;
    const calculateDisable = disabled || (lockIfNotDraft && !isDraft);
    const currentValue = entity[props.name];

    const hasLimit = typeof maxLength === 'number' && maxLength > 0;
    const displayValue = entity[props.name] || defaultValue;

    useEffect(() => {
        if (typeof currentValue !== 'string') return;
        const normalized = normalizeWhitespace(currentValue);
        if (normalized !== currentValue) {
            onChange({ [props.name]: normalized });
        }
    }, [currentValue]);

    const change = (event) => {
        let value = normalizeWhitespace(event.target.value);
        if (regex) {
            value = value.replace(regex, '');
        }
        if (slugify) {
            value = slugifyAltText(value);
        }
        if (typeof maxLength === 'number' && maxLength > 0) {
            value = value.slice(0, maxLength);
        }
        onChange({
            [props.name]: value,
        });
    };

    return (
        <div className={style.formGroup}>
            <Field {...props} disabled={calculateDisable}>
                {(hasError, attributes, isChanged) => (
                    <>
                        <div
                            className={`${style.inputWithPrefix} ${
                                hasError ? style.isInvalid : ''
                            } ${isChanged ? style.changedInput : ''}`}
                        >
                            {prefix && (
                                <span className={style.prefix}>{prefix}</span>
                            )}
                            <input
                                className={style.formTextInput}
                                type="text"
                                onChange={change}
                                value={displayValue}
                                maxLength={maxLength || undefined}
                                autoComplete="off"
                                {...attributes}
                            />
                        </div>

                        {hasLimit && (
                            <CharCounter
                                current={String(displayValue || '').length}
                                max={maxLength}
                            />
                        )}
                    </>
                )}
            </Field>
        </div>
    );
};

export default TextInput;

TextInput.propTypes = {
    /**
     * Type of onChange
     */
    onChange: PropTypes.func.isRequired,
    /**
     * Type of entity
     */
    entity: PropTypes.object.isRequired,
    /**
     * Type of regex
     */
    regex: PropTypes.instanceOf(RegExp),
    /**
     * Type of name
     */
    name: PropTypes.string.isRequired,
    /**
     * Type of changedFields
     */
    changedFields: PropTypes.array,
    /**
     * Type of defaultValue
     */
    defaultValue: PropTypes.string,
    prefix: PropTypes.string,
    slugify: PropTypes.bool,
    disabled: PropTypes.bool,
    lockIfNotDraft: PropTypes.bool,
    maxLength: PropTypes.number,
};

TextInput.defaultProps = {
    regex: null,
    changedFields: [],
    defaultValue: '',
    prefix: null,
    slugify: false,
    disabled: false,
    lockIfNotDraft: false,
    maxLength: null,
};
